import { ButtonPrimary } from "components/Atoms/Button";
import { CompanyHeader } from "components/PurpleHeader/CompanyHeader";
import { HotelHeader } from "components/PurpleHeader/HotelHeader";
import React from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { DetailSidePanel } from "../DetailSidePanel";
import { CompanySidePanelDetails } from "./CompanySidePanelDetails";
import { RightClickMenu } from "components/RightClickMenu/RightClickMenuContext";
import { rightMenuForSidePanel } from "components/RightClickMenu/rightClickMenuUtils";
import { useOpenSidePanel } from "../SidePanel";

export type CompanySidePanelType = "company" | "promoter" | "artist" | "venue" | "hotel";

interface CompanySidePanelProps {
  companyId: number
  type: CompanySidePanelType
  trigger: JSX.Element
  onOpen?: () => void
  fallbackIfNoPermission?: JSX.Element
}

const getCompanySidePanelLabel = (type: CompanySidePanelType) => {
  switch (type) {
    case "company":
      return RESX.Company.resxCompany;
    case "promoter":
      return site.currentEnvironment.functionGroups.promoter.singular;
    case "artist":
      return site.currentEnvironment.functionGroups.artist.singular;
    case "venue":
      return site.currentEnvironment.functionGroups.venue.singular;
    case "hotel":
      return site.currentEnvironment.functionGroups.hotel.singular;
  }
}

export const CompanySidePanel = (props: CompanySidePanelProps) => {
  if (!site.permissions.addressBook.canAccess) {
    return props.fallbackIfNoPermission || props.trigger;
  }

  return <DetailSidePanel
    detailUrl={routes.company_Detail(props.companyId).absolute}
    apiCall={() => window.CoreApi.Company.GetSidePanelData(props.companyId)}
    apiKey={props.type}
    entityId={props.companyId}
    title={getCompanySidePanelLabel(props.type)}
    headerContent={data => props.type === "hotel" ? <HotelHeader company={data.company} /> : <CompanyHeader company={data.company} />}
    footerContent={
      <ButtonPrimary href={routes.company_Detail(props.companyId).absolute} label={RESX.Buttons.DetailView} />
    }
    onOpen={props.onOpen}
    trigger={<RightClickMenu menuItems={rightMenuForSidePanel(routes.company_Detail(props.companyId).absolute)}>{props.trigger}</RightClickMenu>}
  >
    {(company, refetch) => <CompanySidePanelDetails
      company={company}
      type={props.type}
      refetch={refetch}
    />}
  </DetailSidePanel>;
};

export const useOpenCompanySidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openCompanySidePanel = React.useCallback((companyId: number, type: CompanySidePanelType) => {
    openSidePanel(<DetailSidePanel
      apiCall={() => window.CoreApi.Company.GetSidePanelData(companyId)}
      apiKey={type}
      entityId={companyId}
      title={getCompanySidePanelLabel(type)}
      headerContent={data => type === "hotel" ? <HotelHeader company={data.company} /> : <CompanyHeader company={data.company} />}
      footerContent={
        <ButtonPrimary href={routes.company_Detail(companyId).absolute} label={RESX.Buttons.DetailView} />
      }
      skipSidePanelBase
    >
      {(data, refetch) => <CompanySidePanelDetails
        company={data}
        type={type}
        refetch={refetch}
      />}
    </DetailSidePanel>, `${type}-side-${companyId}`);

  }, [openSidePanel]);

  return openCompanySidePanel;
}
